/* global window */

import { object } from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';

import { ToastContext } from './context/ToastContext';
import WalletPaymentMethod from './WalletPaymentMethod';
import { CustomerContext } from './context/CustomerContext';
import AccountAddress from './AccountAddress';

import '../styles/WalletPaymentMethods.css';

function WalletPaymentMethods({ user }) {
    const { sendMessage } = useContext(ToastContext);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [links, setLinks] = useState(null);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const { customerData } = useContext(CustomerContext);

    const onNewPaymentMethod = () => {
        if (!links?.addPaymentMethod?.href) {
            sendMessage('Cannot add payment method. Missing target URL', 'danger');
        }
        const width = 600;
        const height = 400;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        const popupWindow = window.open(
            links.addPaymentMethod.href,
            'popupWindow',
            `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
        );
        const interval = setInterval(() => {
            if (popupWindow.closed) {
                clearInterval(interval);
                fetchPaymentMethods();
            }
        }, [400]);
    };

    const fetchPaymentMethods = useCallback(async () => {
        if (!user) {
            return;
        }
        try {
            const url = new URL(`/api/user/${user.id}/paymentMethods`, process.env.APP_URL);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            const { paymentMethods, _links, error } = await response.json();
            if (error) {
                sendMessage(error, 'danger');
            }
            if (paymentMethods && _links) {
                setPaymentMethods(paymentMethods);
                setLinks(_links);
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoading(false);
        }
    }, [user]);

    const fetchCountries = useCallback(async () => {
        try {
            const url = new URL('/api/countries', process.env.APP_URL);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            const { error, countries } = await response.json();
            if (error) {
                sendMessage(error, 'danger');
            }
            if (countries) {
                setCountries(countries);
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        }
    }, []);

    const fetchStateData = useCallback(async (countryCode) => {
        try {
            const response = await fetch(process.env.APP_URL + 'api/states?page=0&countryCode=' + countryCode, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (data.states) {
                setStates(data.states);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    useEffect(() => {
        fetchCountries();
    }, [fetchCountries]);

    return (
        <div>
            <h3
                style={{ marginBottom: 0 }}
            >
                My addresses
            </h3>
            {customerData.MailingAddresses?.length ? (
                <div
                    className={'wallet-addresses'}
                >
                    {customerData.MailingAddresses?.map(address => (
                        <AccountAddress
                            key={address.id}
                            id={address.id}
                            firstName={address.firstName}
                            lastName={address.lastName}
                            company={address.company}
                            address1={address.address1}
                            city={address.city}
                            zip={address.zip}
                            state={address.State?.stateCode ?? null}
                            country={address.Country?.title ?? null}
                            isDefault={address.isDefault}
                        />
                    ))}
                </div>
            ) : <p>Account has no addresses.</p>}
            <h3
                className={'mb-4'}
            >
                My payment methods
            </h3>
            {loading ? <div>Loading payment methods...</div> : null}
            {!loading && !paymentMethods.length ? <div>No payment methods for this account</div> : null}
            <div
                className={'wallet-payment-methods'}
            >
                {paymentMethods.map(paymentMethod => (
                    <WalletPaymentMethod
                        key={paymentMethod.id}
                        paymentMethod={paymentMethod}
                        userId={user.id}
                        fetchPaymentMethods={fetchPaymentMethods}
                        countries={countries}
                    />
                ))}
            </div>
            {!loading ? (
                <Button
                    onClick={onNewPaymentMethod}
                >
                    Add new payment method
                </Button>
            ) : null}
        </div>
    );
}

WalletPaymentMethods.propTypes = {
    user: object,
};

export default WalletPaymentMethods;